import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  TextField,
  Snackbar,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Create as CreateIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { normalizeSignatureRequest, pendingSignerEmails } from '../utils/eSignature';
import {
  fieldsForSigner,
  fieldDisplayLabel,
  isInkFieldType,
  isMergeField,
  normalizeEsignField,
  signerCollectableFields
} from '../utils/esignFields';
import SignerFieldsForm from './esign/SignerFieldsForm';
import PdfScrollPreview from './esign/PdfScrollPreview';

/**
 * Public signing page — live /api/e-signature (ZRF-ESIGN-01 + BYO fields ZRF-ESIGN-02).
 * Prefers PDF flow when available; otherwise records signature via /sign/:id.
 * Query `?email=` selects the invited signer (required for dual-sign inspection reports).
 */
const PublicSignaturePage = () => {
  const { signatureRequestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfUrlRaw, setPdfUrlRaw] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [signaturePageIndex, setSignaturePageIndex] = useState(null);
  const [usePdfSign, setUsePdfSign] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerRole, setSignerRole] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  const [signatureType, setSignatureType] = useState('draw');
  const [esignFields, setEsignFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  useEffect(() => {
    fetchSignatureRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureRequestId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && signatureType === 'draw') {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [signatureType]);

  const fetchSignatureRequest = async () => {
    if (!signatureRequestId) {
      setError('Missing signature request id');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Status is the source of truth for hub-created + PDF requests
      const statusRes = await api.get(`/api/e-signature/status/${signatureRequestId}`);
      const normalized = normalizeSignatureRequest(statusRes.data || {});
      setRequest(normalized);

      if (statusRes.data?.linkClosed) {
        setError(statusRes.data.closedMessage || 'This signing link is no longer active.');
        return;
      }
      if (normalized.status === 'completed') {
        setError(
          'This signing invitation was already completed. Open the newest email invite (or ask the landlord to click “Send new signature invites”). Old links cannot be re-used.'
        );
      } else if (normalized.status === 'cancelled' || normalized.status === 'superseded') {
        setError(
          'This signing invitation was replaced. Open the newest email invite from the landlord.'
        );
      } else if (normalized.status === 'expired') {
        setError('This signature request is expired.');
      }

      const pending = pendingSignerEmails(normalized);
      const invitedEmail = String(searchParams.get('email') || '')
        .trim()
        .toLowerCase();
      const invitedRole = String(searchParams.get('role') || '')
        .trim()
        .toLowerCase();

      const pick =
        (invitedEmail &&
          (pending.find((s) => String(s.email || '').toLowerCase() === invitedEmail) ||
            (normalized.signers || []).find(
              (s) => String(s.email || '').toLowerCase() === invitedEmail
            ))) ||
        (invitedRole &&
          (pending.find((s) => String(s.role || '').toLowerCase() === invitedRole) ||
            (normalized.signers || []).find(
              (s) => String(s.role || '').toLowerCase() === invitedRole
            ))) ||
        pending[0] ||
        normalized.signers?.[0];

      if (pick) {
        setSignerName(pick.name || '');
        setSignerEmail(pick.email || '');
        setSignerRole(pick.role || invitedRole || '');
        setEmailLocked(Boolean(invitedEmail || invitedRole));
      }

      const rawFields = Array.isArray(normalized.fields)
        ? normalized.fields
        : Array.isArray(statusRes.data?.fields)
          ? statusRes.data.fields
          : [];
      setEsignFields(rawFields.map(normalizeEsignField));
      const initialValues = {};
      rawFields.forEach((f) => {
        if (f.type === 'dateSigned') {
          initialValues[f.id] = new Date().toISOString().slice(0, 10);
        }
        if (f.type === 'name' && pick?.name) {
          initialValues[f.id] = pick.name;
        }
      });
      setFieldValues(initialValues);

      // Optional PDF preview / PDF sign path
      try {
        const pdfRes = await api.get(`/api/e-signature/pdf/${signatureRequestId}`);
        if (pdfRes.data?.pdfUrl) {
          // Preview from page 1 so checklist + photo evidence are visible; keep #page= link for jump
          const raw = pdfRes.data.pdfUrlRaw || String(pdfRes.data.pdfUrl).split('#')[0];
          setPdfUrlRaw(raw);
          setPdfUrl(pdfRes.data.pdfUrl);
          // Prefer API-streamed file (no Storage CORS / iframe scroll bugs)
          const filePath = pdfRes.data.pdfFilePath;
          const base = (api.defaults.baseURL || '').replace(/\/$/, '');
          setPdfPreviewUrl(filePath ? `${base}${filePath}` : raw);
          setUsePdfSign(true);
          if (pdfRes.data.signaturePageIndex != null) {
            setSignaturePageIndex(Number(pdfRes.data.signaturePageIndex));
          }
          if (Array.isArray(pdfRes.data?.fields) && pdfRes.data.fields.length) {
            setEsignFields(pdfRes.data.fields.map(normalizeEsignField));
          }
        }
      } catch (pdfErr) {
        console.log('🔍 No PDF for this request; using status + /sign flow', pdfErr?.response?.status);
        setPdfUrl('');
        setPdfUrlRaw('');
        setPdfPreviewUrl('');
        setUsePdfSign(false);
        setSignaturePageIndex(null);
      }
    } catch (err) {
      console.error('❌ Error fetching signature request:', err);
      setError(err.response?.data?.error || 'Failed to load signature request');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e) => {
    if (signatureType !== 'draw') return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.beginPath();
    ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
  };

  const draw = (e) => {
    if (!isDrawing || signatureType !== 'draw') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.lineTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      captureSignature();
    }
  };

  const captureSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  const handleTypedSignature = (e) => {
    const value = e.target.value;
    setSignerName(value);
    if (value.trim()) {
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.font = '48px "Segoe Script", "Brush Script MT", cursive';
      ctx.fillStyle = '#000000';
      ctx.fillText(value, 20, 60);
      setSignatureData(canvas.toDataURL('image/png'));
    } else {
      setSignatureData(null);
    }
  };

  const pointerToClient = (e) => {
    if (e.touches?.[0]) {
      return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
    }
    return { clientX: e.clientX, clientY: e.clientY };
  };

  const handleSubmit = async () => {
    const assigned = fieldsForSigner(esignFields, { email: signerEmail, role: signerRole });
    const collectable = signerCollectableFields(esignFields, {
      email: signerEmail,
      role: signerRole
    });
    const hasCollectable = collectable.length > 0;

    if (!hasCollectable && !signatureData) {
      setSnackbar({ open: true, message: 'Please provide a signature', severity: 'error' });
      return;
    }
    if (hasCollectable) {
      const missing = collectable.filter((f) => {
        if (f.required === false || isMergeField(f)) return false;
        const v = fieldValues[f.id];
        if (isInkFieldType(f.type)) return !v;
        if (f.type === 'checkbox') {
          return !(v === true || v === 'true' || v === 1 || v === '1' || v === 'yes');
        }
        return !String(v || '').trim();
      });
      if (missing.length) {
        setSnackbar({
          open: true,
          message: `Please complete: ${missing.map((f) => fieldDisplayLabel(f)).join(', ')}`,
          severity: 'error'
        });
        return;
      }
    }
    if (!signerName.trim()) {
      setSnackbar({ open: true, message: 'Please enter your name', severity: 'error' });
      return;
    }
    if (!signerEmail.trim()) {
      setSnackbar({ open: true, message: 'Please select or enter your signer email', severity: 'error' });
      return;
    }

    // Prefer primary signature ink from BYO signature field when present
    const primaryInk =
      signatureData ||
      collectable
        .filter((f) => f.type === 'signature')
        .map((f) => fieldValues[f.id])
        .find(Boolean) ||
      collectable
        .filter((f) => f.type === 'initials')
        .map((f) => fieldValues[f.id])
        .find(Boolean) ||
      null;

    setSubmitting(true);
    try {
      if (usePdfSign) {
        const signRes = await api.post(`/api/e-signature/pdf/${signatureRequestId}/sign`, {
          signerEmail: signerEmail.trim(),
          signatureImage: primaryInk,
          fieldValues: hasCollectable || assigned.length ? fieldValues : undefined,
          ipAddress: '',
          userAgent: navigator.userAgent
        });
        if (signRes.data?.signedPdfUrl) {
          const pageHint =
            signaturePageIndex != null ? `#page=${Number(signaturePageIndex) + 1}` : '';
          setPdfUrl(`${signRes.data.signedPdfUrl}${pageHint}`);
        }
      } else {
        // Non-PDF hub requests: processSignature looks up Firestore doc id when possible
        const signId = request?.firestoreId || signatureRequestId;
        await api.post(`/api/e-signature/sign/${signId}`, {
          signerEmail: signerEmail.trim(),
          signatureData: {
            signature: primaryInk,
            name: signerName.trim(),
            fieldValues: hasCollectable || assigned.length ? fieldValues : undefined,
            ipAddress: '',
            userAgent: navigator.userAgent
          }
        });
      }

      setSuccess(true);
      setSnackbar({ open: true, message: 'Document signed successfully!', severity: 'success' });
      setTimeout(() => {
        navigate(`/signature-success?requestId=${encodeURIComponent(signatureRequestId)}`);
      }, 1500);
    } catch (err) {
      console.error('❌ Error submitting signature:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to submit signature',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading document…</Typography>
      </Container>
    );
  }

  if (error && !request) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Document Signed Successfully!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Thank you for signing. Redirecting…
        </Typography>
      </Container>
    );
  }

  const alreadyDone = error && request;
  const pending = pendingSignerEmails(request || {});
  const myFields = fieldsForSigner(esignFields, { email: signerEmail, role: signerRole });
  const collectableFields = signerCollectableFields(esignFields, {
    email: signerEmail,
    role: signerRole
  });
  const hasCollectable = collectableFields.length > 0;
  const hasByoFields = myFields.length > 0;
  const hasDocumentCheckboxes = myFields.some((f) => f.type === 'checkbox');
  const mergePreview =
    (request && (request.mergeValues || request.resolvedMergeValues)) || {};
  const canSubmit =
    !submitting &&
    Boolean(signerName.trim()) &&
    Boolean(signerEmail.trim()) &&
    (hasCollectable
      ? collectableFields.every((f) => {
          if (f.required === false || isMergeField(f)) return true;
          const v = fieldValues[f.id];
          if (isInkFieldType(f.type)) return Boolean(v);
          if (f.type === 'checkbox') {
            return v === true || v === 'true' || v === 1 || v === '1' || v === 'yes';
          }
          return Boolean(String(v || '').trim());
        })
      : Boolean(signatureData));

  const setFieldValue = (id, value) => {
    setFieldValues((prev) => ({ ...prev, [id]: value }));
  };

  const focusSignPanel = () => {
    const el = document.getElementById('sign-panel');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Sign Document
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        {request?.documentTitle || 'Please review and provide your signature'}
      </Typography>
      {request?.message && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {request.message}
        </Alert>
      )}
      {alreadyDone && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Signing is turned off for this link. Use the newest invite email, or ask the landlord to
            send new signature invites.
          </Typography>
        </Alert>
      )}

      {!alreadyDone && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Use the <strong>Your signature</strong> card (draw or type, then Sign Document). The PDF
          preview is for review only — it is not the signing pad.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* With BYO checkboxes: document first so signers tap acknowledgments in context */}
        <Grid
          item
          xs={12}
          md={5}
          order={{ xs: hasDocumentCheckboxes ? 2 : 1, md: 2 }}
        >
          <Card
            id="sign-panel"
            sx={{
              position: { md: 'sticky' },
              top: { md: 16 },
              border: '2px solid',
              borderColor: alreadyDone ? 'warning.main' : 'primary.main'
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your signature
              </Typography>

              {alreadyDone ? (
                <Alert severity="warning">
                  This invite cannot accept a signature. Open the newest email link from the landlord.
                </Alert>
              ) : (
                <>
                  {signerRole && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You are signing as <strong>{String(signerRole)}</strong>
                      {signerEmail ? ` (${signerEmail})` : ''}.
                    </Alert>
                  )}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Your email (as invited)</InputLabel>
                    <Select
                      value={signerEmail}
                      label="Your email (as invited)"
                      disabled={emailLocked && Boolean(signerEmail)}
                      onChange={(e) => {
                        const email = e.target.value;
                        setSignerEmail(email);
                        const match = (request?.signers || []).find((s) => s.email === email);
                        if (match?.name) setSignerName(match.name);
                        if (match?.role) setSignerRole(match.role);
                      }}
                    >
                      {(pending.length ? pending : request?.signers || []).map((s) => (
                        <MenuItem key={s.email} value={s.email}>
                          {s.role
                            ? `${String(s.role)} · ${s.name || s.email}`
                            : s.name
                              ? `${s.name} · ${s.email}`
                              : s.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {hasByoFields && (
                    <SignerFieldsForm
                      fields={myFields}
                      values={fieldValues}
                      onChange={setFieldValues}
                      disabled={submitting}
                      mergePreview={mergePreview}
                      hideTypes={hasDocumentCheckboxes ? ['checkbox'] : []}
                    />
                  )}

                  {!hasCollectable && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Draw or type your signature in the box, then tap Sign Document.
                      </Typography>
                      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant={signatureType === 'draw' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            setSignatureType('draw');
                            setSignatureData(null);
                          }}
                          startIcon={<CreateIcon />}
                        >
                          Draw
                        </Button>
                        <Button
                          variant={signatureType === 'type' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            setSignatureType('type');
                            setSignatureData(null);
                          }}
                        >
                          Type
                        </Button>
                      </Box>

                      {signatureType === 'draw' && (
                        <Box>
                          <Paper
                            sx={{
                              border: '2px solid #ddd',
                              borderRadius: 1,
                              mb: 2,
                              cursor: 'crosshair',
                              backgroundColor: '#fff'
                            }}
                          >
                            <canvas
                              ref={canvasRef}
                              width={400}
                              height={200}
                              onMouseDown={startDrawing}
                              onMouseMove={draw}
                              onMouseUp={stopDrawing}
                              onMouseLeave={stopDrawing}
                              onTouchStart={(e) => {
                                e.preventDefault();
                                startDrawing(pointerToClient(e));
                              }}
                              onTouchMove={(e) => {
                                e.preventDefault();
                                draw(pointerToClient(e));
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                stopDrawing();
                              }}
                              style={{
                                width: '100%',
                                height: '200px',
                                display: 'block',
                                touchAction: 'none'
                              }}
                            />
                          </Paper>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={clearSignature}
                            startIcon={<ClearIcon />}
                            sx={{ mb: 2 }}
                          >
                            Clear Signature
                          </Button>
                        </Box>
                      )}

                      {signatureType === 'type' && (
                        <TextField
                          fullWidth
                          label="Type Your Name"
                          value={signerName}
                          onChange={handleTypedSignature}
                          sx={{ mb: 2 }}
                          placeholder="Your full name"
                        />
                      )}
                    </>
                  )}

                  <TextField
                    fullWidth
                    label="Print Name (Required)"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    required
                    sx={{ mb: 3 }}
                  />

                  <Divider sx={{ my: 2 }} />

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  >
                    {submitting ? 'Signing…' : 'Sign Document'}
                  </Button>

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    By signing, you acknowledge that you have read and agree to the terms of this document.
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={7}
          order={{ xs: hasDocumentCheckboxes ? 1 : 2, md: 1 }}
        >
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Document preview
              </Typography>
              {signaturePageIndex != null && !hasByoFields && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Scroll to review checklist and photo evidence. Signature lines in the PDF are on page{' '}
                  {Number(signaturePageIndex) + 1} — use the <strong>Your signature</strong> box to sign
                  in ZenRentFlow.
                </Alert>
              )}
              {hasByoFields && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {hasDocumentCheckboxes
                    ? 'Tap each checkbox box on the PDF to read that acknowledgment in place. Signature and printed name are in Your signature below.'
                    : 'Colored boxes show where your answers will land. Complete them in Your signature — values stamp onto the PDF after you sign.'}
                </Alert>
              )}
              {pdfPreviewUrl || pdfUrlRaw || pdfUrl ? (
                <PdfScrollPreview
                  url={pdfPreviewUrl || pdfUrlRaw || String(pdfUrl).split('#')[0]}
                  fallbackUrl={pdfUrlRaw || String(pdfUrl || '').split('#')[0] || null}
                  openUrl={pdfPreviewUrl || pdfUrlRaw || String(pdfUrl).split('#')[0]}
                  openSignatureUrl={
                    signaturePageIndex != null && !hasByoFields ? pdfUrl : null
                  }
                  fields={hasByoFields ? esignFields : null}
                  fieldValues={fieldValues}
                  mergePreview={mergePreview}
                  highlightSigner={{ email: signerEmail, role: signerRole }}
                  interactiveTypes={['checkbox']}
                  disabled={submitting || Boolean(alreadyDone)}
                  onFieldValueChange={setFieldValue}
                  onFieldActivate={(field) => {
                    if (isInkFieldType(field.type) || field.type === 'name' || field.type === 'text') {
                      focusSignPanel();
                    }
                  }}
                />
              ) : (
                <Alert severity="info">
                  No PDF preview for this request. You can still sign in the signature box — the landlord
                  will see your status update in Documents → Signatures.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PublicSignaturePage;
